import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createScheduleItem, fetchPartners, fetchSchedules } from "api";
import { PageHeader, FormField, Spinner, Select } from "components/common";
import type { PartnerEntity } from "types";

export default function NewScheduleItemForm() {
  const navigate       = useNavigate();
  const queryClient    = useQueryClient();
  const [searchParams] = useSearchParams();

  const [partnerEntityId, setPartnerEntityId] = useState(
    searchParams.get("partner_entity_id") ?? ""
  );
  const [scheduledAt, setScheduledAt] = useState("");

  // Multi-value deliver_to / received_from
  const [deliverToIds,    setDeliverToIds]    = useState<number[]>([]);
  const [receivedFromIds, setReceivedFromIds] = useState<number[]>([]);
  const [deliverToAdd,    setDeliverToAdd]    = useState("");
  const [receivedFromAdd, setReceivedFromAdd] = useState("");

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const { data: schedules = [] } = useQuery({
    queryKey: ["schedules", partnerEntityId],
    queryFn:  () => fetchSchedules({ partner_entity_id: partnerEntityId }),
    enabled:  !!partnerEntityId,
  });

  const distributors = partners.filter((p: PartnerEntity) => p.type === "distributor" && p.active);
  const donators     = partners.filter((p: PartnerEntity) => p.type === "donator"      && p.active);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      createScheduleItem({
        partner_entity_id: Number(partnerEntityId),
        scheduled_at:      new Date(scheduledAt).toISOString(),
        status:            "created",
        ...(deliverToIds.length    > 0 && { deliver_to_ids:    deliverToIds    } as any),
        ...(receivedFromIds.length > 0 && { received_from_ids: receivedFromIds } as any),
      } as any),
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: ["scheduleItems"] });
      navigate("/schedule-items/" + item.id);
    },
  });

  const isValid = partnerEntityId && scheduledAt;

  const addDeliverTo = () => {
    const id = Number(deliverToAdd);
    if (id && !deliverToIds.includes(id)) setDeliverToIds((prev) => [...prev, id]);
    setDeliverToAdd("");
  };

  const addReceivedFrom = () => {
    const id = Number(receivedFromAdd);
    if (id && !receivedFromIds.includes(id)) setReceivedFromIds((prev) => [...prev, id]);
    setReceivedFromAdd("");
  };

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-lg">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors"
      >
        ← Back
      </button>

      <PageHeader
        title="Add scheduled item"
        subtitle="Manually create a one-off pickup or drop-off."
      />

      <form
        onSubmit={(e) => { e.preventDefault(); if (isValid) mutate(); }}
        className="card p-6 space-y-5"
      >
        <FormField label="Partner">
          <Select value={partnerEntityId} onChange={(e) => setPartnerEntityId(e.target.value)}>
            <option value="" disabled>Select a partner…</option>
            {[...distributors, ...donators].map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Date and time">
          <input
            className="input"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            required
          />
        </FormField>

        {/* ── Deliver to (multi) ────────────────────────────────────────── */}
        <FormField label="Deliver to">
          <div className="space-y-1">
            {deliverToIds.map((id) => {
              const p = distributors.find((d) => d.id === id);
              return p ? (
                <div key={id} className="flex items-center gap-2">
                  <span className="text-sm text-gray-800">{p.name}</span>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-500 text-xs"
                    onClick={() => setDeliverToIds((prev) => prev.filter((i) => i !== id))}
                  >
                    ×
                  </button>
                </div>
              ) : null;
            })}
            <div className="flex items-center gap-2">
              <Select
                size="sm"
                className="w-44"
                value={deliverToAdd}
                onChange={(e) => setDeliverToAdd(e.target.value)}
              >
                <option value="">Add distributor…</option>
                {distributors
                  .filter((p) => !deliverToIds.includes(p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </Select>
              <button
                type="button"
                className="btn-secondary py-1 px-2 text-xs"
                disabled={!deliverToAdd}
                onClick={addDeliverTo}
              >
                Add
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            The distributor partner(s) receiving the food.
          </p>
        </FormField>

        {/* ── Received from (multi) ─────────────────────────────────────── */}
        <FormField label="Received from">
          <div className="space-y-1">
            {receivedFromIds.map((id) => {
              const p = donators.find((d) => d.id === id);
              return p ? (
                <div key={id} className="flex items-center gap-2">
                  <span className="text-sm text-gray-800">{p.name}</span>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-500 text-xs"
                    onClick={() => setReceivedFromIds((prev) => prev.filter((i) => i !== id))}
                  >
                    ×
                  </button>
                </div>
              ) : null;
            })}
            <div className="flex items-center gap-2">
              <Select
                size="sm"
                className="w-44"
                value={receivedFromAdd}
                onChange={(e) => setReceivedFromAdd(e.target.value)}
              >
                <option value="">Add donator…</option>
                {donators
                  .filter((p) => !receivedFromIds.includes(p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </Select>
              <button
                type="button"
                className="btn-secondary py-1 px-2 text-xs"
                disabled={!receivedFromAdd}
                onClick={addReceivedFrom}
              >
                Add
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            The donator partner(s) the food came from.
          </p>
        </FormField>

        {error && (
          <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || isPending}>
            {isPending ? <Spinner className="h-4" /> : "Create item"}
          </button>
        </div>
      </form>
    </div>
  );
}
